import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { loadFlags, saveFlags } from './flagStore';

interface ExamFlagsValue {
  flagged: ReadonlySet<string>;
  toggle: (questionId: string) => void;
}

const NONE: ExamFlagsValue = { flagged: new Set(), toggle: () => undefined };
const ExamFlagsContext = createContext<ExamFlagsValue>(NONE);

/** Flag state for the whole runner, so the question views, palettes and the review grid agree. */
export function ExamFlagsProvider({ attemptId, children }: { attemptId: string; children: React.ReactNode }) {
  const [flagged, setFlagged] = useState<ReadonlySet<string>>(() => new Set(loadFlags(attemptId)));
  useEffect(() => saveFlags(attemptId, flagged), [attemptId, flagged]);
  const toggle = useCallback((questionId: string) => {
    setFlagged((prev) => {
      const next = new Set(prev);
      if (!next.delete(questionId)) next.add(questionId);
      return next;
    });
  }, []);
  const value = useMemo(() => ({ flagged, toggle }), [flagged, toggle]);
  return <ExamFlagsContext.Provider value={value}>{children}</ExamFlagsContext.Provider>;
}

export const useExamFlags = (): ExamFlagsValue => useContext(ExamFlagsContext);
